import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase.ts';
import type {
  GroundTruthV1,
  Submission,
  SubmissionFile,
  SubmissionPage,
  TakeoffDiscipline,
  TakeoffSessionRow,
  TakeoffStatus,
} from './types.ts';

const SUBMISSIONS_BUCKET = 'pdf-parser-submissions';

export async function listSubmissions(): Promise<Submission[]> {
  const { data, error } = await supabase
    .from('submissions')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listSubmissions failed: ${error.message}`);
  return (data ?? []) as Submission[];
}

function sessionHasTakeoffData(discipline: string, jobData: Record<string, unknown>): boolean {
  if (!jobData || Object.keys(jobData).length === 0) return false;
  if (discipline === 'structural') {
    return Array.isArray(jobData.segments) && (jobData.segments as unknown[]).length > 0;
  }
  if (discipline === 'civil') {
    const pages = jobData.pages;
    if (!pages || typeof pages !== 'object') return false;
    return Object.values(pages as Record<string, unknown>).some((page) => {
      const items = (page as Record<string, unknown>).items;
      return Array.isArray(items) && (items as unknown[]).length > 0;
    });
  }
  return false;
}

export async function listSubmissionsWithTakeoffData(): Promise<Submission[]> {
  const [subResult, sessResult] = await Promise.all([
    supabase.from('submissions').select('*').order('created_at', { ascending: false }),
    supabase.from('takeoff_sessions').select('submission_id,discipline,job_data'),
  ]);
  if (subResult.error) throw new Error(`listSubmissionsWithTakeoffData failed: ${subResult.error.message}`);
  if (sessResult.error) throw new Error(`listSubmissionsWithTakeoffData failed: ${sessResult.error.message}`);

  const qualifiedIds = new Set<string>();
  for (const sess of (sessResult.data ?? [])) {
    const row = sess as { submission_id: string; discipline: string; job_data: Record<string, unknown> };
    if (sessionHasTakeoffData(row.discipline, row.job_data)) {
      qualifiedIds.add(row.submission_id);
    }
  }

  return (subResult.data ?? []).filter((s) => qualifiedIds.has((s as { id: string }).id)) as Submission[];
}

export async function getSubmission(id: string): Promise<Submission | null> {
  const { data, error } = await supabase.from('submissions').select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(`getSubmission failed: ${error.message}`);
  return (data ?? null) as Submission | null;
}

export async function listSubmissionPages(submissionId: string): Promise<SubmissionPage[]> {
  const { data, error } = await supabase
    .from('submission_pages')
    .select('*')
    .eq('submission_id', submissionId)
    .order('page_index', { ascending: true });
  if (error) throw new Error(`listSubmissionPages failed: ${error.message}`);
  return (data ?? []) as SubmissionPage[];
}

export async function listSubmissionFiles(submissionId: string): Promise<SubmissionFile[]> {
  const { data, error } = await supabase
    .from('submission_files')
    .select('*')
    .eq('submission_id', submissionId)
    .order('discipline', { ascending: true });
  if (error) throw new Error(`listSubmissionFiles failed: ${error.message}`);
  return (data ?? []) as SubmissionFile[];
}

export interface CreateSubmissionInput {
  jobName: string;
  sourcePdfFilename: string;
  userId: string;
}

export async function createSubmission(input: CreateSubmissionInput): Promise<Submission> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('submissions')
    .insert({
      job_name: input.jobName,
      source_pdf: input.sourcePdfFilename,
      user_id: input.userId,
      kind: 'production',
      status: 'uploading',
      submitted_at: now,
    })
    .select('*')
    .single();
  if (error) throw new Error(`createSubmission failed: ${error.message}`);
  return data as Submission;
}

export async function uploadSubmissionPdf(
  submissionId: string,
  file: File,
): Promise<{ path: string }> {
  const path = `${submissionId}/${file.name}`;
  const { error } = await supabase.storage.from(SUBMISSIONS_BUCKET).upload(path, file, {
    contentType: file.type || 'application/pdf',
    upsert: true,
  });
  if (error) throw new Error(`uploadSubmissionPdf failed: ${error.message}`);
  return { path };
}

export async function markSubmissionSubmitted(submissionId: string): Promise<void> {
  const { error } = await supabase
    .from('submissions')
    .update({ status: 'submitted' })
    .eq('id', submissionId);
  if (error) throw new Error(`markSubmissionSubmitted failed: ${error.message}`);
}

export async function getSubmissionFileSignedUrl(
  storagePath: string,
  expiresInSeconds = 60 * 5,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(SUBMISSIONS_BUCKET)
    .createSignedUrl(storagePath, expiresInSeconds);
  if (error || !data?.signedUrl) {
    throw new Error(`signed URL failed: ${error?.message ?? 'no url returned'}`);
  }
  return data.signedUrl;
}

interface SubscribeOptions {
  submissionId: string;
  onSubmissionChange: (next: Submission) => void;
  onPagesChange?: () => void;
  onFilesChange?: () => void;
}

export function subscribeToSubmission(opts: SubscribeOptions): RealtimeChannel {
  const channel = supabase
    .channel(`submission:${opts.submissionId}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'submissions', filter: `id=eq.${opts.submissionId}` },
      (payload) => opts.onSubmissionChange(payload.new as Submission),
    );

  if (opts.onPagesChange) {
    channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'submission_pages', filter: `submission_id=eq.${opts.submissionId}` },
      () => opts.onPagesChange?.(),
    );
  }
  if (opts.onFilesChange) {
    channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'submission_files', filter: `submission_id=eq.${opts.submissionId}` },
      () => opts.onFilesChange?.(),
    );
  }

  channel.subscribe();
  return channel;
}

export async function getSourcePdfSignedUrl(
  submissionId: string,
  sourcePdf: string,
  expiresInSeconds = 60 * 30,
): Promise<string> {
  const storagePath = `${submissionId}/${sourcePdf}`;
  const { data, error } = await supabase.storage
    .from(SUBMISSIONS_BUCKET)
    .createSignedUrl(storagePath, expiresInSeconds);
  if (error || !data?.signedUrl) {
    throw new Error(`source PDF signed URL failed: ${error?.message ?? 'no url returned'}`);
  }
  return data.signedUrl;
}

export async function updatePageReview(
  pageId: string,
  decision: import('./types.ts').ReviewDecision,
  humanCategory: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('submission_pages')
    .update({ review_decision: decision, review_human_category: humanCategory })
    .eq('id', pageId);
  if (error) throw new Error(`updatePageReview failed: ${error.message}`);
}

// ── Corpus promotion (Inc 7 Phase 2) ────────────────────────────────────────

// Atomic promote: kind+ground_truth land in one UPDATE so the corpus_requires_truth
// CHECK constraint is satisfied. Returns the updated row to confirm the write
// landed (RLS-silent zero-rows lesson from Phase 1).
export async function promoteToCorpus(
  submissionId: string,
  groundTruth: GroundTruthV1,
): Promise<Submission> {
  const { data, error } = await supabase
    .from('submissions')
    .update({ kind: 'corpus', ground_truth: groundTruth })
    .eq('id', submissionId)
    .select('*')
    .single();
  if (error) throw new Error(`promoteToCorpus failed: ${error.message}`);
  if (!data) throw new Error('promoteToCorpus failed: no row returned (likely RLS)');
  return data as Submission;
}

export async function revertToProduction(submissionId: string): Promise<Submission> {
  const { data, error } = await supabase
    .from('submissions')
    .update({ kind: 'production', ground_truth: null })
    .eq('id', submissionId)
    .select('*')
    .single();
  if (error) throw new Error(`revertToProduction failed: ${error.message}`);
  if (!data) throw new Error('revertToProduction failed: no row returned (likely RLS)');
  return data as Submission;
}

export async function listCorpusSubmissions(): Promise<Submission[]> {
  const { data, error } = await supabase
    .from('submissions')
    .select('*')
    .eq('kind', 'corpus')
    .order('updated_at', { ascending: false });
  if (error) throw new Error(`listCorpusSubmissions failed: ${error.message}`);
  return (data ?? []) as Submission[];
}

export interface CorpusJobWithPages {
  submission: Submission;
  pages: readonly SubmissionPage[];
}

// One round-trip: fetch every corpus submission together with its pages so
// the training dashboard can grade them all without N+1 queries. Pages come
// nested under each submission row from PostgREST and are flattened here.
export async function listCorpusJobsWithPages(): Promise<CorpusJobWithPages[]> {
  const { data, error } = await supabase
    .from('submissions')
    .select('*, submission_pages(*)')
    .eq('kind', 'corpus')
    .order('updated_at', { ascending: false });
  if (error) throw new Error(`listCorpusJobsWithPages failed: ${error.message}`);
  const rows = (data ?? []) as ReadonlyArray<Submission & { submission_pages: SubmissionPage[] }>;
  return rows.map((row) => {
    const { submission_pages: pages, ...submission } = row;
    const sortedPages = [...(pages ?? [])].sort((a, b) => a.page_index - b.page_index);
    return { submission: submission as Submission, pages: sortedPages };
  });
}

export function subscribeToSubmissionList(onChange: () => void): RealtimeChannel {
  const channel = supabase
    .channel('submissions:list')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'submissions' }, () => onChange())
    .subscribe();
  return channel;
}

// ── Takeoff sessions (Increment 4) ──────────────────────────────────────────

export async function getTakeoffSession(
  submissionId: string,
  discipline: TakeoffDiscipline,
): Promise<TakeoffSessionRow | null> {
  const { data, error } = await supabase
    .from('takeoff_sessions')
    .select('*')
    .eq('submission_id', submissionId)
    .eq('discipline', discipline)
    .maybeSingle();
  if (error) throw new Error(`getTakeoffSession failed: ${error.message}`);
  return (data ?? null) as TakeoffSessionRow | null;
}

export interface UpsertTakeoffSessionInput {
  submissionId: string;
  userId: string;
  discipline: TakeoffDiscipline;
  jobData: Record<string, unknown>;
  weightSummary?: Record<string, unknown> | null;
  status?: TakeoffStatus;
}

export async function upsertTakeoffSession(
  input: UpsertTakeoffSessionInput,
): Promise<TakeoffSessionRow> {
  const { data, error } = await supabase
    .from('takeoff_sessions')
    .upsert(
      {
        submission_id: input.submissionId,
        user_id: input.userId,
        discipline: input.discipline,
        job_data: input.jobData,
        weight_summary: input.weightSummary ?? null,
        status: input.status ?? 'draft',
      },
      { onConflict: 'submission_id,discipline' },
    )
    .select('*')
    .single();
  if (error) throw new Error(`upsertTakeoffSession failed: ${error.message}`);
  return data as TakeoffSessionRow;
}

export async function listTakeoffSessions(
  submissionId: string,
): Promise<TakeoffSessionRow[]> {
  const { data, error } = await supabase
    .from('takeoff_sessions')
    .select('*')
    .eq('submission_id', submissionId);
  if (error) throw new Error(`listTakeoffSessions failed: ${error.message}`);
  return (data ?? []) as TakeoffSessionRow[];
}
